/*
 * Luggage Finder -- scoring engine + <luggage-finder-tool> component.
 *
 * ONE file on purpose. The engine half below is pure (no DOM, no network) and
 * exported, so `node --test` can import it directly with no build step. The
 * component half is defined inside a browser guard at the bottom, so this same
 * file imports cleanly under Node. Splitting it would mean a second asset and a
 * relative import resolved against Shopify's CDN -- an avoidable unknown.
 *
 * The \u2264 escapes below are deliberate: literal special characters get
 * normalised by editors and consoles, and these strings must match the
 * metafield choice lists byte for byte or nothing matches.
 */

export const SIZE_ORDER = [
  "Personal Item/Small Carry-on",
  "International Carry-on (\u226421.5\")",
  "USA Domestic Carry-on (\u226422\")",
  "Oversized Carry-on (>22\")",
  "Checked",
  "Large Checked",
];

const CARRYON_TIERS = new Set(SIZE_ORDER.slice(0, 4));
const CHECKED_TIERS = new Set(SIZE_ORDER.slice(4));
const LIFETIME_VENDORS = new Set(["Briggs & Riley", "Victorinox", "Osprey"]);

// Weights. Carry style and budget are near-deal-breakers; nothing eliminates.
const W = {
  carry: 30, side: 20, budget: 25, sizeExact: 15, sizeAdjacent: 7,
  feature: 6, color: 8, brandLiked: 5, warranty: 8, featured: 3, bestseller: 2,
  // Weighted comparably to side (20), but -- unlike side -- summed into the
  // soft-extras bucket below and clamped by SOFT_CAP, per the spec: this
  // axis must never be able to overturn a carry-style mismatch on its own,
  // the way side is structurally prevented from doing so by carry's larger
  // direct weight. Routing it through the same cap as the other nice-to-haves
  // is what keeps that promise for style specifically.
  style: 20,
};
// Soft extras (features, colour, liked brand, warranty, featured, bestseller)
// are nice-to-haves, never dealbreakers. Their combined contribution is
// clamped below carry's weight so a wrong-shape bag can never outscore a
// right-shape one purely by racking up extras -- clamped on the sum, not per
// item, so adding more feature options later can't widen the ceiling.
const SOFT_CAP = 20;
// Best Value floor is relative to the top score, not an absolute number, so
// sparse answer sets (which can't reach a high absolute score) still surface
// a Best Value badge. The top result trivially clears its own floor.
const VALUE_SCORE_FRACTION = 0.6;

export function sizeGroup(tier) {
  if (CARRYON_TIERS.has(tier)) return "carryon";
  if (CHECKED_TIERS.has(tier)) return "checked";
  return null;
}

// Color bucketing: ~200 vendor colourway names collapsed into the 8 buckets
// shown on the color question. This is the inverse of TB-emboss-options.liquid's
// resolveTagColor (which maps a bag color ONTO one of ~24 foil names) -- per
// the spec (2026-08-14-luggage-finder-design.md, "Color matching") it gets its
// own table here rather than reusing that one, since a third consumer of
// resolveTagColor would drag in the embossing system's CLAUDE.md sync
// obligations for no benefit.
//
// Ported from the Python reference implementation
// (tools/luggage-finder/pool.py's _COLOR_BUCKETS, used by the tagging
// script) and extended against the real 171-product fixture
// (tools/luggage-finder/tests/fixtures/pool.json) -- see engine.test.mjs for
// the coverage assertion this table exists to satisfy. Checked in order;
// first bucket whose keyword appears as a substring of the lowercased color
// wins, so a multi-tone name like "Torrent Blue / Barley Brown" resolves to
// whichever bucket is listed first (Blue/Navy, here). Silver/Metallic is
// checked ahead of Grey specifically so "Titanium Grey" -- the only
// metallic-ish color in the real catalog -- lands there rather than being
// swallowed by Grey's plain "grey" match.
const COLOR_BUCKETS = [
  ["Black", ["black", "onyx"]],
  ["Blue/Navy", ["navy", "blue", "ocean", "cobalt", "indigo"]],
  ["Silver/Metallic", ["silver", "metallic", "gunmetal", "platinum", "pewter", "titanium"]],
  ["Grey", ["grey", "gray", "slate", "steel", "charcoal", "granite"]],
  ["Green", ["green", "olive", "rainforest", "forest", "sage", "moss", "pine"]],
  ["Red/Burgundy", ["red", "ruby", "burgundy", "wine", "merlot", "crimson", "bordeaux"]],
  ["Brown/Tan", ["brown", "tan", "khaki", "sand", "cognac", "chocolate", "bronze", "espresso", "expresso", "canyon"]],
  ["Bold/Print", ["print", "floral", "camo", "plaid", "pink", "purple", "yellow", "orange", "plum", "fig", "mango"]],
];

export function colorBucket(rawName) {
  const v = String(rawName || "").trim().toLowerCase();
  // "Default Title" is Shopify's synthetic option value for a product with
  // no real option (i.e. no color option at all) -- treat it as no color,
  // never as a bucket match.
  if (!v || v === "default title") return null;
  for (const [bucket, words] of COLOR_BUCKETS) {
    if (words.some((w) => v.includes(w))) return bucket;
  }
  return null;
}

export function isEligible(product, answers = {}) {
  if (!product.available || !product.image) return false;
  const avoided = answers.brandsAvoided || [];
  if (avoided.includes(product.vendor)) return false;
  if (answers.size) {
    // No recorded size means we cannot prove the group -- exclude rather than guess.
    if (!product.size) return false;
    if (sizeGroup(product.size) !== sizeGroup(answers.size)) return false;
  }
  return true;
}

export function scoreProduct(product, answers = {}) {
  let s = 0;
  if (answers.carry && product.wheels) {
    s += product.wheels === answers.carry ? W.carry : 0;
  }
  if (answers.side && product.side) {
    // N/A is absence of the attribute, not a mismatch: neutral, never negative.
    if (product.side === "N/A") s += W.side / 2;
    else if (product.side === answers.side) s += W.side;
  }
  if (answers.size && product.size) {
    const want = SIZE_ORDER.indexOf(answers.size);
    const got = SIZE_ORDER.indexOf(product.size);
    if (got === want) s += W.sizeExact;
    else if (Math.abs(got - want) === 1) s += W.sizeAdjacent;
  }
  if (answers.budget && typeof product.price === "number") {
    const [lo, hi] = answers.budget;
    if (product.price >= lo && product.price <= hi) {
      s += W.budget;
    } else if (product.price > hi) {
      // Graded, not binary: a bag $50 over the stated ceiling is a much
      // closer match than one $300 over, even though neither is a strict
      // "yes" -- a flat zero for both let a wildly-over-budget bag rank
      // above a barely-over one purely because it happened to win on shape.
      // Decays linearly to zero over a grace window as wide as the budget
      // band itself, floored at zero so an extreme price never goes
      // negative. Below-lo stays a flat miss, same as before -- grading was
      // only asked for the over-budget direction.
      const graceWindow = Math.max(hi - lo, 1);
      const over = product.price - hi;
      s += W.budget * Math.max(0, 1 - over / graceWindow);
    }
  }
  // Soft extras: summed separately from the hard-ish signals above, then
  // clamped as a group so no combination of nice-to-haves can out-muscle a
  // carry-style or side-type mismatch.
  let extras = 0;
  if (answers.features && answers.features.length && product.features) {
    for (const f of answers.features) if (product.features.includes(f)) extras += W.feature;
  }
  // Compare colour *buckets*, not raw strings -- product.colors holds raw
  // vendor colourway names ("Night Shift Blue", "Barley Brown", ...), never
  // one of the 8 bucket labels a customer picks from. A product matches if
  // ANY of its available colours falls in the chosen bucket.
  // Accepts either a single bucket or a list of them (the screen became
  // multi-select on 2026-08-27; a stored single value still scores the same).
  // The bonus is awarded once however many buckets are named -- picking five
  // colours must not out-score picking one, since a shopper listing several
  // is being less fussy, not more deserving.
  if (answers.color && product.colors) {
    const wanted = Array.isArray(answers.color) ? answers.color : [answers.color];
    if (wanted.length && product.colors.some((c) => wanted.includes(colorBucket(c)))) {
      extras += W.color;
    }
  }
  if (answers.brandsLiked && answers.brandsLiked.includes(product.vendor)) {
    extras += W.brandLiked;
  }
  if ((answers.frequency === "frequent" || answers.frequency === "very")
      && LIFETIME_VENDORS.has(product.vendor)) {
    extras += W.warranty;
  }
  if (product.featured) extras += W.featured;
  if (product.bestseller) extras += W.bestseller;
  // Casual/Outdoors vs Business/Professional (custom.finder_style). Deliberately
  // scoped to the extras group, not a direct addition like side above -- see
  // the comment on W.style. Ordering, most to least: an exact match (full
  // credit) > a "Both" product (three-quarter credit -- a dedicated bag
  // should still edge out a crossover, but a crossover must beat everything
  // else) > no style data at all (neutral, half credit, same treatment as
  // N/A side type) > the opposite style (zero, no addition).
  if (answers.style) {
    if (!product.style) extras += W.style / 2;
    else if (product.style === answers.style) extras += W.style;
    else if (product.style === "Both") extras += W.style * 0.75;
  }
  s += Math.min(extras, SOFT_CAP);
  return s;
}

export function rank(products, answers = {}) {
  return products
    .filter((p) => isEligible(p, answers))
    .map((p) => ({ p, s: scoreProduct(p, answers) }))
    // id breaks ties so the same inputs always produce the same order
    .sort((a, b) => b.s - a.s || String(a.p.id).localeCompare(String(b.p.id)))
    .map((x) => x.p);
}

// Fallback when rank() finds nothing eligible (a real possibility once a
// shopper stacks a size, a budget and an avoided brand). Reuses the same
// scoring the ranked results use, just with only the *relaxable* eligibility
// rule (size group) lifted -- per the spec (spec:363-365) brand exclusion is
// a stated refusal and is never relaxed, even on this fallback path, so
// "closest" never means "the brand you just told us to avoid." Pure and
// exported (unlike the rest of the component) so it's testable against real
// catalog data without a DOM -- see the "never recommends an avoided brand"
// test in engine.test.mjs, which is exactly what would have caught this
// filtering only on available/image with no brand check.
export function closestMatches(products, answers = {}) {
  const avoided = answers.brandsAvoided || [];
  const pool = (products || [])
    .filter((p) => p.available && p.image && !avoided.includes(p.vendor));
  return pool
    .map((p) => ({ p, s: scoreProduct(p, answers) }))
    .sort((a, b) => b.s - a.s || String(a.p.id).localeCompare(String(b.p.id)))
    .slice(0, 4)
    .map((x) => x.p);
}

// Reserves a genuinely in-budget alternative when the top overall match
// misses the stated budget (2026-08-19 budget rework -- the owner's real
// catalog audit: his whole hardside carry-on range is $425-$799, so a
// hardside/22"/under-$300 search used to return only expensive hardside
// bags with no cheaper option anywhere in the results). Searches the WHOLE
// pool, not the size/side-filtered `rank()` output -- the point is to admit
// "a different size tier or side type" per the brief, so this deliberately
// applies only the non-relaxable base filters (available, has an image,
// brand not avoided -- the same three closestMatches() never relaxes)
// rather than isEligible()'s size-group rule. Scored with the customer's
// full answers so a candidate that *also* happens to match size/side still
// wins over one that doesn't, among those actually in budget. Pure and
// exported so it's testable without a DOM (see engine.test.mjs and the
// real-catalog case in personas.test.mjs). Returns null when nothing in the
// pool falls inside [lo, hi] at all, or when no budget was stated.
export function pickInBudgetAlternative(products, answers = {}, excludeIds = []) {
  if (!answers.budget) return null;
  const [lo, hi] = answers.budget;
  const avoided = answers.brandsAvoided || [];
  const excluded = new Set(excludeIds);
  const candidates = (products || []).filter((p) => (
    p.available && p.image && !avoided.includes(p.vendor)
    && !excluded.has(p.id)
    && typeof p.price === "number" && p.price >= lo && p.price <= hi
  ));
  if (!candidates.length) return null;
  return candidates
    .map((p) => ({ p, s: scoreProduct(p, answers) }))
    .sort((a, b) => b.s - a.s || String(a.p.id).localeCompare(String(b.p.id)))[0].p;
}

// Honest copy for the reserved in-budget card: names whichever of size/side
// had to give, rather than presenting a size/side swap as if it were an
// exact match. Falls back to a plain statement when the pick happens to
// share size and side with what was asked (it can still win the reserved
// slot purely because rank()'s own top result -- Best -- is a different
// product that's over budget).
export function budgetPickNote(product, answers = {}) {
  const notes = [];
  if (answers.size && product.size && product.size !== answers.size) {
    notes.push(`it's ${labelForSize(product.size)}, not the size you picked`);
  }
  if (answers.side && product.side && product.side !== "N/A" && product.side !== answers.side) {
    notes.push(`it's ${product.side.toLowerCase()}, not ${product.side === "Hardside" ? "softside" : "hardside"}`);
  }
  if (!notes.length) return "The best pick that actually falls inside your stated budget.";
  return `Not an exact match -- ${notes.join(" and ")} -- but it's the best pick that actually falls inside your stated budget.`;
}

// Plain statement for when NOTHING in the whole catalog falls inside the
// stated budget for the stated size/side -- per the owner: "We'd rather
// tell a customer 'we don't stock hardside under $300' than look like we
// ignored the question." Values are always whole dollars (every budget
// dropdown option is a round figure -- see QUESTIONS below), so plain
// division needs no currency formatter and stays importable under Node.
export function noInBudgetMessage(answers = {}) {
  const budget = answers.budget;
  if (!budget) return "";
  const [lo, hi] = budget;
  const dollars = (cents) => `$${Math.round(cents / 100)}`;
  const hasMin = lo > 0;
  const hasMax = hi < 99999999;
  let range = "in that price range";
  if (hasMin && hasMax) range = `between ${dollars(lo)} and ${dollars(hi)}`;
  else if (hasMax) range = `under ${dollars(hi)}`;
  else if (hasMin) range = `over ${dollars(lo)}`;

  const descriptors = [];
  if (answers.side && answers.side !== "N/A") descriptors.push(answers.side.toLowerCase());
  if (answers.size) descriptors.push(labelForSize(answers.size));
  const what = descriptors.length ? descriptors.join(" ") : "a bag";

  return `We don't currently stock ${what} ${range}. These are the closest matches to everything else you told us.`;
}

// Alternatives that actually demonstrate the shop sells bags at more than
// one price point, rather than "the next three by score" -- which tends to
// cluster tightly around Best's own price since score and price both track
// how well a bag matches the stated shape. `candidates` is expected
// best-first (rank()'s own order, or a slice of it) so "the top of a price
// band" means the highest-scoring product in that band, not an arbitrary
// one. Bands are relative to `referencePrice` (Best's price), not absolute,
// so this works the same whether Best is a $90 backpack or a $1,000
// hardside spinner. A band with nothing in it is left empty rather than
// backfilled from a neighboring band -- the brief is explicit that padding
// with a near-duplicate is worse than showing fewer, honestly spread,
// alternatives. Pure and exported so it's testable without a DOM.
export function pickPriceSpreadAlternatives(candidates, referencePrice, count = 3) {
  if (!Array.isArray(candidates) || typeof referencePrice !== "number") return [];
  const priced = candidates.filter((p) => typeof p.price === "number");
  const below = priced.filter((p) => p.price <= referencePrice * 0.8);
  const near = priced.filter((p) => p.price > referencePrice * 0.8 && p.price <= referencePrice * 1.2);
  const above = priced.filter((p) => p.price > referencePrice * 1.2);

  const picks = [];
  const seen = new Set();
  for (const band of [below, near, above]) {
    if (picks.length >= count) break;
    const top = band[0];
    if (top && !seen.has(top.id)) {
      picks.push(top);
      seen.add(top.id);
    }
  }
  return picks;
}

export function badgesFor(product, ranked, answers = {}) {
  const out = [];
  if (ranked.length && product.id === ranked[0].id) out.push("Best");
  if (ranked.length) {
    // Relative floor: a fraction of the top score, not a fixed number, so
    // sparse answer sets (which cap out low in absolute terms) can still
    // produce a Best Value pick. The top result always clears its own floor.
    const floor = scoreProduct(ranked[0], answers) * VALUE_SCORE_FRACTION;
    const eligibleForValue = ranked.filter((p) => scoreProduct(p, answers) >= floor);
    const cheapest = eligibleForValue.reduce((a, b) => (b.price < a.price ? b : a));
    if (cheapest.id === product.id) out.push("Best Value");
  }
  if (product.bestseller) out.push("Best Seller");
  if (LIFETIME_VENDORS.has(product.vendor)) out.push("Lifetime Warranty");
  return out;
}

// Pure predicate the component uses to decide whether to show the
// out-of-budget note on the results screen (a customer stated a budget and
// the price under discussion falls outside it, in either direction).
// Exported so it's testable against real catalog data without a DOM.
export function isOverBudget(product, answers = {}) {
  if (!answers.budget || typeof product.price !== "number") return false;
  const [lo, hi] = answers.budget;
  return product.price < lo || product.price > hi;
}

export function explain(product, answers = {}) {
  const why = [];
  if (product.featured && product.featuredNote) why.push(product.featuredNote);
  if (answers.carry && product.wheels === answers.carry) {
    why.push(`It's the ${labelForWheels(product.wheels)} you asked for.`);
  }
  if (answers.size && product.size === answers.size) {
    why.push(`Sized as ${labelForSize(product.size)}.`);
  } else if (answers.size && product.size) {
    why.push(`Close to your size -- it's ${labelForSize(product.size)}.`);
  }
  if (answers.budget && product.price >= answers.budget[0] && product.price <= answers.budget[1]) {
    why.push("Within your budget.");
  }
  if (LIFETIME_VENDORS.has(product.vendor)) {
    why.push(`${product.vendor} covers functional damage for the life of the bag.`);
  }
  return why;
}

export function labelForWheels(stored) {
  // Customer-facing labels drop the parenthetical negatives. Stored values are
  // never rewritten -- display text and stored data are separate concerns.
  return {
    "Backpack (no wheels)": "backpack",
    "Handheld or Shoulder (no wheels)": "handheld or shoulder bag",
    "4-Wheel Spinner": "4-wheel spinner",
    "2-Wheel Rolling": "2-wheel roller",
  }[stored] || stored;
}

// "Checked" reads as vague sitting next to "Large Checked" on screen, so the
// owner wants it shown as "Medium Checked" -- but the stored tier stays
// exactly "Checked": it's written on 14 live products and is a choice-list
// value in the custom.finder_size_category metafield definition, and
// renaming it would mean migrating live product data and editing that
// Shopify definition, which this display-only ask doesn't justify. Same
// split as labelForWheels() above: display text and stored values are
// deliberately separate, so every other tier round-trips unchanged and an
// unrecognized string is returned as-is rather than becoming undefined.
export function labelForSize(stored) {
  return { Checked: "Medium Checked" }[stored] || stored;
}

/*
 * Question model + product loader.
 *
 * QUESTIONS drives both the wizard UI and, indirectly, the engine above (the
 * "size" question's options are built from SIZE_ORDER so the tiers shown to a
 * shopper can never drift from the tiers scoreProduct() actually scores).
 */

export const QUESTIONS = [
  { id: "frequency", type: "single", skippable: true,
    title: "How often do you travel?",
    options: [
      { label: "Once or twice a year", value: "occasional" },
      { label: "Several times a year", value: "frequent" },
      { label: "Constantly -- it's my job", value: "very" },
    ] },
  { id: "carry", type: "single", skippable: true,
    title: "How do you want to carry it?",
    options: [
      { label: "Backpack", value: "Backpack (no wheels)" },
      { label: "4-wheel spinner", value: "4-Wheel Spinner" },
      { label: "2-wheel roller", value: "2-Wheel Rolling" },
      { label: "Handheld or shoulder", value: "Handheld or Shoulder (no wheels)" },
    ] },
  { id: "side", type: "single", skippable: true,
    title: "Hardside or softside?",
    options: [
      { label: "Hardside", value: "Hardside" },
      { label: "Softside", value: "Softside" },
    ] },
  // Mirrors "side" above -- mutually exclusive with it on carry (see
  // visibleQuestions below), so exactly one of the two is ever on screen at
  // position 3. Added because a wheels-less bag has no hardside/softside
  // axis to ask about at all, yet the finder had no other signal to tell an
  // Osprey hiking pack from an @Work briefcase (both backpacks/handhelds).
  // Only two options on purpose -- "Both" is a property a *bag* can have
  // (custom.finder_style), never something a shopper picks for themselves.
  { id: "style", type: "single", skippable: true,
    title: "What kind of bag is this for?",
    options: [
      { label: "Casual or outdoors", value: "Casual / Outdoors" },
      { label: "Work or business", value: "Business / Professional" },
    ] },
  { id: "size", type: "single", skippable: true,
    title: "How big?",
    options: SIZE_ORDER.map((tier) => ({ label: labelForSize(tier), value: tier })) },
  // Two dropdowns (floor + ceiling) instead of four fixed bands, so "$200 to
  // $600" is expressible instead of forced into whichever preset band it
  // straddles (2026-08-19 rework, per the owner's real-catalog audit --
  // range chosen against the fixture's actual $42-$1,125 spread; see
  // tools/luggage-finder/tests/fixtures/pool.json). "Not sure" is a plain
  // `single`-shaped option (value: null) rendered alongside the two
  // dropdowns by renderBudgetOptions() below -- reusing the existing
  // data-select handling rather than a bespoke skip control, since setting
  // answers.budget to null already means "no budget preference" everywhere
  // downstream (scoreProduct, isOverBudget, pickInBudgetAlternative all
  // treat a falsy answers.budget as unanswered). Whichever dropdown value
  // the customer confirms still reaches the engine as the same [lo, hi]
  // shape the four-band version used, so scoreProduct()/isOverBudget() need
  // no changes at all.
  { id: "budget", type: "range", skippable: true,
    title: "What's your budget?",
    min: {
      label: "At least",
      options: [
        { label: "No minimum", value: 0 },
        { label: "$150", value: 15000 },
        { label: "$300", value: 30000 },
        { label: "$500", value: 50000 },
        { label: "$750", value: 75000 },
      ],
    },
    max: {
      label: "At most",
      options: [
        { label: "$150", value: 15000 },
        { label: "$300", value: 30000 },
        { label: "$500", value: 50000 },
        { label: "$750", value: 75000 },
        { label: "$1,000", value: 100000 },
        { label: "No maximum", value: 99999999 },
      ],
    },
    notSure: { label: "Not sure — no budget preference", value: null } },
  { id: "features", type: "multi", skippable: true,
    title: "Anything it must have?",
    options: [
      "Laptop Compartment", "Garment Sleeve", "Expandable", "Built-in TSA Lock",
      "Anti-Theft/RFID Blocking", "Large Main Compartment", "Organized Packing Pockets",
    ].map((f) => ({ label: f, value: f })) },
  // Multi-select since 2026-08-27 (owner). Most shoppers will take any of
  // two or three colours, and forcing one made the answer arbitrary. Colour
  // is a soft, capped extra either way, so allowing several costs the ranking
  // nothing -- see the once-only bonus in scoreProduct above.
  //
  // notSure gives "no preference" a button of its own rather than leaving it
  // to "Skip this question", which reads as abandoning the quiz rather than
  // answering it. Same pattern, and the same null value, as the budget screen.
  { id: "color", type: "multi", skippable: true,
    title: "Do you have a color preference?",
    options: ["Black", "Blue/Navy", "Grey", "Green", "Red/Burgundy",
              "Brown/Tan", "Silver/Metallic", "Bold/Print"]
      .map((c) => ({ label: c, value: c })),
    notSure: { label: "No preference", value: null } },
  // Two-part screen per spec:257 -- brands you like (soft boost, writes
  // answers.brandsLiked) and brands to avoid (hard exclusion, writes
  // answers.brandsAvoided), mutually exclusive per brand. Rendered by
  // renderBrandsOptions() below rather than the generic single/multi
  // renderer, since it needs two independent multi-select groups sharing one
  // option list. type: "brands" is a signal to the component, not the
  // engine -- scoreProduct()/isEligible() only ever read the two answer keys
  // it writes, never `answers.brands`.
  { id: "brands", type: "brands", skippable: true,
    title: "Any brands you love -- or would rather avoid?",
    options: ["Briggs & Riley", "Travelpro", "Osprey", "Victorinox",
              "Ricardo Beverly Hills", "Brics"].map((b) => ({ label: b, value: b })) },
];

// Hardside/softside is meaningless for a bag with no hard/soft distinction, and
// 34% of the pool is exactly that. Only ask it when the answer could matter.
const WHEELED = new Set(["4-Wheel Spinner", "2-Wheel Rolling"]);

// "style" is the mirror image of "side": asked only for the two definite
// non-wheeled carry answers, never for a wheeled answer and never for "no
// preference" (unlike "side", which IS asked for "no preference" -- these
// two questions are deliberately not symmetric on that point, per spec).
const NON_WHEELED = new Set(["Backpack (no wheels)", "Handheld or Shoulder (no wheels)"]);

export function visibleQuestions(answers = {}) {
  return QUESTIONS.filter((q) => {
    if (q.id === "side") return !answers.carry || WHEELED.has(answers.carry);
    if (q.id === "style") return NON_WHEELED.has(answers.carry);
    return true;
  });
}

// If a later answer makes an earlier-answered question's choice inapplicable
// -- today that's only "side" going hidden when carry stops being wheeled --
// the stale answer must not survive to keep nudging scoreProduct(). Pure and
// generic over any question, so a future conditional question is covered for
// free rather than needing its own special case.
export function pruneHiddenAnswers(answers = {}) {
  const visibleIds = new Set(visibleQuestions(answers).map((q) => q.id));
  const pruned = { ...answers };
  for (const id of Object.keys(pruned)) {
    if (QUESTIONS.some((q) => q.id === id) && !visibleIds.has(id)) {
      delete pruned[id];
    }
  }
  return pruned;
}

const CACHE_KEY = "luggage-finder-products-v1";

// Determines which theme's Section Rendering API endpoint a customer's
// browser must target. `/?section_id=...` is root-relative, so during a
// storefront preview (an unpublished theme) it only reaches that theme
// while Shopify's preview cookie is attached to the request; once that
// lapses -- or the fetch happens in a context that doesn't carry it -- the
// SAME url is served by the *published* theme instead, which has no
// luggage-finder-data section. That's this bug: it works once, then fails
// for good. Shopify exposes window.Shopify.theme (id + role; role is
// "main" only for the published theme) on every storefront page, so when
// role isn't "main" the request is pinned to that theme explicitly via
// ?preview_theme_id=<id>. Falls back to reading preview_theme_id off the
// current URL when window.Shopify/theme isn't available, and to no param
// at all -- today's exact behavior -- when neither signal is present.
// Takes the Shopify global and location.search as plain arguments rather
// than reading window itself, so it stays pure, side-effect free, and
// testable under `node --test` with no DOM -- and it never throws while
// building a URL, wrapping each lookup so a malformed global or query
// string just falls through to the next fallback.
export function resolvePreviewThemeId(shopifyGlobal, locationSearch) {
  try {
    const theme = shopifyGlobal && shopifyGlobal.theme;
    if (theme && theme.role && theme.role !== "main" && theme.id != null) {
      return String(theme.id);
    }
  } catch (err) {
    // Unexpected shape for window.Shopify -- fall through to the query string.
  }
  try {
    const fromQuery = new URLSearchParams(locationSearch || "").get("preview_theme_id");
    if (fromQuery) return fromQuery;
  } catch (err) {
    // Malformed search string -- no signal available, use the plain url.
  }
  return null;
}

// The section-feed url for one page, targeted at a specific theme when one
// is known. A no-op -- today's exact url, no extra param -- when
// previewThemeId is null/undefined, which is always the case on the live
// theme.
export function buildSectionUrl(page, previewThemeId) {
  const base = `/?section_id=luggage-finder-data&page=${page}`;
  return previewThemeId ? `${base}&preview_theme_id=${encodeURIComponent(previewThemeId)}` : base;
}

// Two different themes can legitimately serve different product data (a
// theme mid-edit with different tags/collections wired up), so a cached
// payload from one theme must never be reused on another. Unsuffixed --
// exactly CACHE_KEY -- when previewThemeId is null, so the live theme's
// existing sessionStorage entries keep working with no migration needed.
function cacheKeyFor(previewThemeId) {
  return previewThemeId ? `${CACHE_KEY}:${previewThemeId}` : CACHE_KEY;
}

// Fetches every page of the /?section_id=luggage-finder-data feed and merges
// them. Two failure shapes matter here, both of which must leave the cache
// untouched rather than poisoning it for the rest of the browser session:
//   - a non-ok response (404, 500, an HTML error page)
//   - a 200 with a body that doesn't match the expected shape (missing
//     `products`, non-numeric `pages`) -- valid JSON, wrong contents
// storage.setItem is only ever called once, after every page has been
// fetched AND validated, so a partial or failed load can't get written.
// `previewThemeId` (optional, defaults to null -- today's behavior) is
// resolved by the caller via resolvePreviewThemeId(); it's taken as a plain
// argument rather than read from globals in here so this function keeps
// importing and running cleanly under plain Node with no DOM.
/*
 * Reading and writing sessionStorage can THROW, not merely fail:
 *  - Safari private browsing reports a quota of zero, so setItem raises
 *    QuotaExceededError even though the API exists.
 *  - iOS tracking prevention and "block all cookies" can make touching
 *    storage at all raise a SecurityError.
 *  - A corrupted entry makes JSON.parse throw.
 *
 * The cache is an optimization: it saves four requests on a repeat visit.
 * It is never worth failing the whole tool for, and an unguarded write is
 * exactly how "it works for me" turns into "no one else can use it".
 */
function cacheRead(storage, key) {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch (err) {
    return null;
  }
}

function cacheWrite(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (err) {
    /* No cache this session. The caller already has the data. */
  }
}

export async function loadProducts(fetchImpl, storage, previewThemeId = null) {
  const cacheKey = cacheKeyFor(previewThemeId);
  const cached = cacheRead(storage, cacheKey);
  if (cached) return cached;

  const url = (page) => buildSectionUrl(page, previewThemeId);

  // Every /?section_id= request goes through Shopify's Section Rendering
  // API, which always wraps the response in
  // <div id="shopify-section-...">...</div> -- confirmed live against
  // theme 165494128852 while capturing this task's fixture, with and
  // without XHR-shaped headers (X-Requested-With, Sec-Fetch-Dest), so
  // response.json() throws a SyntaxError on the real endpoint even though
  // this section's own Liquid emits bare JSON with no wrapper of its own.
  // Slicing between the first "{" and the last "}" strips that envelope
  // without a DOM dependency, so it stays correct whether or not a wrapper
  // is present and keeps this function importable under plain Node.
  function stripSectionWrapper(text) {
    const start = text.indexOf("{");
    const end = text.lastIndexOf("}");
    return start === -1 || end === -1 || end < start ? text : text.slice(start, end + 1);
  }

  async function fetchPage(page) {
    const response = await fetchImpl(url(page));
    if (!response.ok) {
      throw new Error(`Luggage Finder: page ${page} of the product feed failed to load (status ${response.status}).`);
    }
    const text = await response.text();
    try {
      return JSON.parse(stripSectionWrapper(text));
    } catch (err) {
      throw new Error(`Luggage Finder: page ${page} of the product feed returned invalid JSON.`);
    }
  }

  function assertValidPage(page, data) {
    if (!Array.isArray(data.products)) {
      throw new Error(`Luggage Finder: page ${page} of the product feed had no product list.`);
    }
  }

  const first = await fetchPage(1);
  if (typeof first.pages !== "number") {
    throw new Error("Luggage Finder: page 1 of the product feed did not report a page count.");
  }
  assertValidPage(1, first);

  const rest = [];
  for (let page = 2; page <= first.pages; page += 1) rest.push(fetchPage(page));
  const others = await Promise.all(rest);
  others.forEach((data, i) => assertValidPage(i + 2, data));

  const all = [first, ...others].flatMap((d) => d.products);
  cacheWrite(storage, cacheKey, all);
  return all;
}

// Hardside/softside explainer shown only on the "side" question. A plain
// string template with no DOM dependency, so -- unlike the component below
// -- it lives on the pure/exported side of the file and is importable
// directly by node --test (see questions.test.mjs). Collapsed by default
// behind a native <details>/<summary> disclosure: no JS, no ARIA of our own
// -- <details> is already keyboard-operable and announced correctly by
// screen readers. The summary is the prompt itself and must stay visually
// inviting (the owner treats this screen as the one that does real
// selling), even though the copy underneath starts hidden.
// Shown under the question on the screens that accept more than one answer.
//
// Those are also exactly the screens that carry a Continue button, because
// nothing on them can auto-advance -- a single-choice screen moves on the
// moment you tap. Without this line the Continue button reads as the only
// way forward and the fact that you may pick several goes unnoticed
// (owner, 2026-08-26: "add some words to explain to the user they can check
// multiple options if they choose then click continue").
export const MULTI_SELECT_HINT =
  "Choose as many as you like \u2014 then select Continue.";

// "brands" is two multi-select groups on one screen, so it earns the hint
// for the same reason "multi" does. "single" and "range" never show it:
// single advances on tap, and the budget screen's Continue is explained by
// its own "Not sure" option sitting right above it.
export function hintFor(question) {
  if (!question) return "";
  return question.type === "multi" || question.type === "brands"
    ? MULTI_SELECT_HINT
    : "";
}

export const HARDSIDE_EXPLAINER = `
  <div class="luggage-finder__explainer">
    <details class="luggage-finder__explainer-details">
      <summary class="luggage-finder__explainer-summary">Learn the differences between the two</summary>
      <div class="luggage-finder__explainer-body">
        <p><strong>Hardside</strong> bags open like a clamshell, with your things split
        across two halves. You need space to open one fully -- a bed or a clear
        patch of floor -- and once it is packed, getting at something usually
        means opening the whole bag. Most hardside bags have no outside pockets
        either, so a sweater or a rain jacket you want within reach has to go
        inside with everything else.</p>
        <p>In return you get a shell that shrugs off rain and resists being
        crushed under other bags, which earns its keep on luggage that gets
        checked and thrown around.</p>
        <p><strong>Softside</strong> bags flex to take a little extra when you are
        packing tight, weigh less empty, and give you exterior pockets you can
        reach without unpacking anything -- handy for the layer you shed at the
        gate.</p>
        <p class="luggage-finder__explainer-note">Not sure? Most everyday
        travelers do fine with softside. Hardside earns its keep on bags that
        get checked and knocked about a lot.</p>
      </div>
    </details>
  </div>
`;

// Intro copy shown above the FIRST question only (see introContent() below).
// Owner-approved final wording -- do not paraphrase. Kept as named constants
// on the pure/exported side of the file (same treatment the hardside
// explainer above gets) so the visibility/override logic around them is
// testable under `node --test` with no DOM. Staff can override either via
// the section's "Intro heading" /
// "Intro body" settings (see sections/luggage-finder.liquid and
// luggage-finder-tab.liquid), which land on the host element as
// data-copy-intro-heading / data-copy-intro-body -- the same convention
// questionTitle() below uses for per-question headlines.
// Minimal attribute escaping for the two pure builders below. Deliberately a
// separate, distinctly-named helper rather than reusing escapeAttr(): that one
// lives inside the DOM guard further down, and this file is served to the
// storefront as a plain <script>, where hoisting a shared name up to global
// scope risks colliding with the theme's own helpers.
function escapeAttrValue(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// The explainer with an optional comparison photo spliced in above the copy
// (owner, 2026-08-27). Wraps HARDSIDE_EXPLAINER rather than absorbing it so
// the copy itself stays a single exported string the tests assert on directly,
// and so a store with no photo uploaded gets byte-identical output to before.
export function hardsideExplainer(imageUrl, imageAlt) {
  if (!imageUrl) return HARDSIDE_EXPLAINER;
  const alt = imageAlt || "A hardside bag and a softside bag, both open";
  const img = '<img class="luggage-finder__explainer-image" src="'
    + escapeAttrValue(imageUrl) + '" alt="' + escapeAttrValue(alt) + '" loading="lazy">';
  return HARDSIDE_EXPLAINER.replace(
    '<div class="luggage-finder__explainer-body">',
    '<div class="luggage-finder__explainer-body">' + img,
  );
}

// Feeds the companion panel that fills the empty half of a wide question
// screen (owner, 2026-08-27: "so plain and empty... nothing to visually keep
// the interest"). Costs no requests -- the pool is already in memory from
// loadProducts and rank() is pure, so this is a filter and a sort over data
// the browser is already holding.
//
// `count` deliberately reuses rank()/isEligible(), so the panel can never
// advertise a bag the results screen then withholds. Note what it is NOT: a
// count of products matching every answer. Only size, availability and
// avoided brands actually exclude -- carry, side, budget, features and colour
// are scored, not filtered. A strict every-answer count would also read far
// lower than reality while the catalogue's feature and style metafields are
// still being filled in, which would make the tool look emptier than it is.
// Does this product satisfy every answer that can be checked exactly?
//
// This is NOT isEligible(). isEligible() is the engine's own admission rule
// and is deliberately loose -- it groups all four carry-on tiers together, and
// lets carry style, side and budget be scored rather than filtered, so a near
// miss can still be recommended. That looseness is right for choosing what to
// recommend and wrong for a number on screen: measured with isEligible(), the
// live catalogue returns an identical 127 for "Personal Item" and for
// "Oversized Carry-on", and 159 of 159 before any answer at all.
//
// Only dimensions whose data is complete across the whole pool are checked
// (verified 2026-08-27: size, wheels and side are 171/171, price is intrinsic).
// Features are deliberately absent -- 27 of 171 products carry the metafield,
// so filtering on one would report single digits and read as "we barely stock
// anything" when it really means the data entry is unfinished. Colour and
// style are out for the same reason, plus colour matches by fuzzy bucket.
//
// SYNC: the comparisons below mirror scoreProduct()'s. If the way an answer is
// compared to a product changes there -- an option value renamed, a new
// sentinel like side "N/A" -- change it here too, or the count will start
// disagreeing with the recommendations underneath it.
export function matchesExactly(product, answers = {}) {
  if (!product.available || !product.image) return false;
  if ((answers.brandsAvoided || []).includes(product.vendor)) return false;
  if (answers.size && product.size !== answers.size) return false;
  if (answers.carry && product.wheels !== answers.carry) return false;
  // "N/A" is the absence of a side type (a backpack has none), not a
  // mismatch -- scoreProduct() treats it as neutral, so it must not exclude.
  if (answers.side && product.side !== "N/A" && product.side !== answers.side) return false;
  if (answers.budget && typeof product.price === "number") {
    const [lo, hi] = answers.budget;
    if (product.price < lo || product.price > hi) return false;
  }
  return true;
}

// True once the customer has answered something the count can actually narrow
// on. Before that, "159 of 159" is a fact about nothing.
export function hasNarrowingAnswer(answers = {}) {
  return Boolean(
    answers.size || answers.carry || answers.side || answers.budget
    || (answers.brandsAvoided && answers.brandsAvoided.length),
  );
}

export function matchPanel(products, answers = {}, previewCount = 3) {
  const list = products || [];
  const total = list.filter((p) => p.available && p.image).length;
  // Previews stay on rank(), not matchesExactly(): the panel must never sit
  // empty, and these are the same bags, in the same order, that the results
  // screen would lead with. When the exact count is 0 they are honestly
  // labelled as the closest instead.
  const ranked = rank(list, answers);
  return {
    total,
    count: list.filter((p) => matchesExactly(p, answers)).length,
    narrowed: hasNarrowingAnswer(answers),
    previews: ranked.slice(0, previewCount),
  };
}

export const DEFAULT_INTRO_HEADING = "Let us help find your next bag";

// Owner-rewritten 2026-08-20. Verbatim, not a paraphrase -- two changes are
// deliberate corrections of fact, not style: "available to order" (the shop
// special-orders; it does not warehouse stock to pull from) replaces the
// prior "we actually stock", and travel frequency is named explicitly
// because scoreProduct() above does weigh it (the "frequency" answer feeds
// the warranty bonus in the soft-extras bucket, alongside carry/size/budget
// as the other named factors) -- keep both of those facts true if this copy
// is ever reworded.
export const DEFAULT_INTRO_PARAGRAPHS = [
  "A few quick questions and we'll show you what we'd put in your hands if you walked into our store.",
  "We take into account how much you travel, the type of bag you want, the size you need, and your budget most heavily, then use features, color and brand to choose between what's left. Every bag we show you is one that is available to order. Our goal is to find you the best bag for you to travel with.",
  "Not sure about a question? Skip it. You can jump to your matches whenever you like.",
];

// Staff type paragraphs into a plain <textarea> setting separated by a
// blank line -- the natural way to enter multiple paragraphs -- so this
// splits on blank-line boundaries first. Falls back to one paragraph per
// non-blank line for a staff edit that skipped the blank lines, rather than
// rendering the whole override as a single wall-of-text paragraph.
export function splitIntroParagraphs(raw) {
  if (!raw || raw.trim() === "") return [];
  const blocks = raw.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (blocks.length > 1) return blocks;
  return raw.split(/\n/).map((p) => p.trim()).filter(Boolean);
}

// Decides whether the quiz intro renders for the given step, and its
// resolved copy. Pure and exported (unlike the rest of the render path,
// which lives behind the DOM guard below) specifically so this decision --
// first question only, never later questions, never the results screen
// (resultsMarkup()/renderLoadError() are separate methods that never call
// this) -- has real `node --test` coverage despite this repo having no
// jsdom/browser test environment. `enabled` mirrors the section's "Show
// intro" checkbox; blank heading/body override strings fall back to the
// built-in copy, same as questionTitle()'s override convention.
export function introContent(step, { enabled = true, heading, body } = {}) {
  if (step !== 0 || !enabled) return null;
  const paragraphs = body && body.trim() !== "" ? splitIntroParagraphs(body) : [];
  return {
    heading: heading && heading.trim() !== "" ? heading : DEFAULT_INTRO_HEADING,
    paragraphs: paragraphs.length ? paragraphs : DEFAULT_INTRO_PARAGRAPHS,
  };
}

/*
 * <luggage-finder-tool> -- the wizard component.
 *
 * Defined only when a DOM is present so this file keeps importing cleanly
 * under `node --test`. Everything above this guard is pure and exported for
 * that reason; everything below touches the DOM and is intentionally not
 * exported -- Node has no need of it, and the theme loads this file as a
 * plain <script>, not a module.
 */
if (typeof HTMLElement !== "undefined") {
  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    }[ch]));
  }

  // Used inside HTML attributes -- both double-quoted ones (href, alt,
  // aria-label) and the single-quoted data-value attribute that carries a
  // JSON blob. Escapes both quote characters plus & so product data (titles
  // can legitimately contain a literal ") never breaks out of the attribute.
  function escapeAttr(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  const priceFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

  function formatPrice(cents) {
    if (typeof cents !== "number") return "";
    // Intl.NumberFormat, not manual string building, so thousands get a
    // separator ($1,125.00) matching every other price shown on the store --
    // a plain `$${(cents/100).toFixed(2)}` renders $1125.00 instead.
    return priceFormatter.format(cents / 100);
  }

  // Shown on the results screen when the customer stated a budget and the
  // top recommendation falls outside it -- the spec's "thin results get
  // honest framing" rule (already applied to the isClosest note just below)
  // extended to budget specifically. Reworded for the 2026-08-19 budget
  // rework: Best is still the best OVERALL match (never displaced), but a
  // genuinely in-budget pick is now always reserved among the alternatives
  // (see pickInBudgetAlternative()), so this note points there instead of
  // implying Best itself is "the closest option" price-wise. Kept as its
  // own named constant, next to the other customer-facing copy in this
  // file, so it can be reworded without hunting through resultsMarkup()'s
  // template logic.
  const OUT_OF_BUDGET_NOTE = `
    <p class="luggage-finder__results-note luggage-finder__results-note--budget">
      Our best overall match for you costs more than what you told us to
      spend -- so one of the picks below is set aside for the best option
      that actually fits your budget.
    </p>
  `;

  class LuggageFinderTool extends HTMLElement {
    connectedCallback() {
      this.answers = {};
      this.step = 0;
      this.products = null;
      this.productsPromise = null;
      this.resultsShown = false;
      this.pendingMulti = new Set();
      this.pendingBrandsLiked = new Set();
      this.pendingBrandsAvoided = new Set();
      this.pendingBudgetMin = null;
      this.pendingBudgetMax = null;
      this.addEventListener("click", this.handleClick);
      this.render();
      track("luggage_finder_start", {});
      // Warm the pool immediately. It used to be fetched only when the
      // results screen asked for it, which left this.products null for the
      // whole quiz -- so the companion panel had nothing to show and never
      // appeared. Prefetching also removes the wait that used to sit between
      // the last answer and the results.
      //
      // Failure is swallowed on purpose: the panel is an enhancement, and the
      // results screen already reports a failed load properly when it asks
      // for the pool itself.
      this.ensureProducts()
        .then(() => this.refreshPanel())
        .catch(() => {});
    }

    get questions() { return visibleQuestions(this.answers); }

    // Staff can reword a question's on-screen headline via the section's
    // per-question text blocks (rendered onto the host element as
    // data-copy-<id> attributes) without touching this file. Reads the
    // attribute at render time rather than mutating the shared QUESTIONS
    // array, so the override is per-instance and the pure question model
    // stays untouched. Falls through to the built-in title when the
    // attribute is absent or blank -- e.g. no section wrapper (standalone
    // use), or a block left empty in the theme editor.
    questionTitle(question) {
      const override = this.getAttribute(`data-copy-${question.id}`);
      return override && override.trim() !== "" ? override : question.title;
    }

    // Markup for the quiz intro (see introContent() above), or "" once the
    // customer has moved past the first question. Rendered above the whole
    // question block -- progress bar included -- inside render() only, so
    // it can never appear on resultsMarkup()/renderLoadError()'s screens.
    introMarkup() {
      const intro = introContent(this.step, {
        enabled: this.getAttribute("data-intro-enabled") !== "false",
        heading: this.getAttribute("data-copy-intro-heading"),
        body: this.getAttribute("data-copy-intro-body"),
      });
      if (!intro) return "";
      return `
        <div class="luggage-finder__intro">
          <h3 class="luggage-finder__intro-heading">${escapeHtml(intro.heading)}</h3>
          ${intro.paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("")}
        </div>`;
    }

    async ensureProducts() {
      if (this.products) return this.products;
      // Cache the promise, not just the result: the panel prefetch starts on
      // connect and a quick run through the questions can ask for the pool
      // again before that first request has landed. Without this both calls
      // would fetch.
      if (!this.productsPromise) {
        const previewThemeId = resolvePreviewThemeId(window.Shopify, window.location.search);
        this.productsPromise = loadProducts(
          window.fetch.bind(window), window.sessionStorage, previewThemeId,
        );
      }
      this.products = await this.productsPromise;
      return this.products;
    }

    // Swaps the panel in place once the pool arrives mid-question. Deliberately
    // not a full re-render: setScreen() moves focus to the question heading,
    // which would yank a reader back to the top mid-sentence when the fetch
    // happens to resolve.
    refreshPanel() {
      const screen = this.querySelector(".luggage-finder__screen");
      if (!screen) return;
      const html = this.renderPanel();
      const existing = screen.querySelector(".luggage-finder__panel");
      if (existing) existing.remove();
      if (html) screen.insertAdjacentHTML("beforeend", html);
      screen.classList.toggle("luggage-finder__screen--with-panel", Boolean(html));
    }

    answer(id, value) {
      this.answers[id] = value;
      // A changed answer can hide a later question (e.g. switching carry
      // away from wheeled hides "side"). Its stale value must not keep
      // nudging scoreProduct() after it's no longer confirmed on screen.
      this.answers = pruneHiddenAnswers(this.answers);
      track("luggage_finder_step_complete", { step: this.step + 1, question: id, value });
      this.next();
    }

    // The brands screen's Continue button, distinct from answer() because it
    // writes two answer keys (brandsLiked/brandsAvoided) from two pending
    // sets rather than one value under answers[id] -- scoreProduct() and
    // isEligible() only ever read those two keys, never `answers.brands`.
    answerBrands() {
      this.answers.brandsLiked = Array.from(this.pendingBrandsLiked);
      this.answers.brandsAvoided = Array.from(this.pendingBrandsAvoided);
      this.answers = pruneHiddenAnswers(this.answers);
      track("luggage_finder_step_complete", {
        step: this.step + 1,
        question: "brands",
        value: { liked: this.answers.brandsLiked, avoided: this.answers.brandsAvoided },
      });
      this.next();
    }

    // The budget screen's Continue button, distinct from answer() because it
    // reads two <select> elements (min/max) instead of one stored value.
    // Guards against a stated minimum above the stated maximum by
    // normalizing (swapping) rather than blocking Continue in the UI --
    // whichever dropdown the customer touched last, the pair that reaches
    // the engine is always a valid [lo, hi] with lo <= hi.
    answerBudget() {
      const minEl = this.querySelector("[data-budget-min]");
      const maxEl = this.querySelector("[data-budget-max]");
      let lo = Number(minEl.value);
      let hi = Number(maxEl.value);
      if (lo > hi) { const swap = lo; lo = hi; hi = swap; }
      this.answer("budget", [lo, hi]);
    }

    skip() {
      const q = this.questions[this.step];
      track("luggage_finder_step_skip", { step: this.step + 1, question: q.id });
      this.next();
    }

    next() {
      if (this.step < this.questions.length - 1) { this.step += 1; this.render(); }
      else this.showResults();
    }

    back() { if (this.step > 0) { this.step -= 1; this.render(); } }

    // Returns to whichever question the customer was actually on --
    // this.step already holds that (showResults()/showResults(true) never
    // change it), so early exit from, say, the budget screen comes back to
    // the budget screen, not question 8. Previously forced this.step to
    // questions.length - 1, which was only ever correct for a customer who
    // reached results by answering every question in order.
    backFromResults() {
      this.resultsShown = false;
      this.render();
    }

    restart() {
      this.answers = {};
      this.step = 0;
      this.resultsShown = false;
      this.pendingMulti = new Set();
      this.pendingBrandsLiked = new Set();
      this.pendingBrandsAvoided = new Set();
      this.pendingBudgetMin = null;
      this.pendingBudgetMax = null;
      this.render();
      track("luggage_finder_restart", {});
    }

    async showResults(early = false) {
      if (early) track("luggage_finder_early_exit", { step: this.step + 1 });
      let products;
      try {
        products = await this.ensureProducts();
      } catch (err) {
        track("luggage_finder_load_error", { message: err.message });
        this.renderLoadError();
        return;
      }
      const ranked = rank(products, this.answers);
      track("luggage_finder_results_shown", {
        count: ranked.length, top: ranked[0] ? ranked[0].title : null,
      });
      this.renderResults(ranked);
    }

    // "Try again" clears both the in-memory and sessionStorage cache before
    // retrying -- a customer who hits this screen may be carrying a cache
    // written before this fix shipped, or the feed may have been down and
    // is now back, so a bare retry against the same (empty) in-memory state
    // isn't enough on its own.
    retryLoad() {
      try {
        const previewThemeId = resolvePreviewThemeId(window.Shopify, window.location.search);
        window.sessionStorage.removeItem(cacheKeyFor(previewThemeId));
      } catch (err) {
        // sessionStorage can throw in some private-browsing modes; the
        // in-memory cache reset below still lets the retry proceed.
      }
      this.products = null;
      this.productsPromise = null;
      this.showResults();
    }

    handleClick(event) {
      const target = event.target.closest(
        "[data-select], [data-toggle], [data-continue], [data-back], "
        + "[data-skip], [data-see-matches], [data-restart], [data-results-back], "
        + "[data-retry], [data-result-click], [data-toggle-liked], "
        + "[data-toggle-avoided], [data-continue-brands], [data-continue-budget]",
      );
      if (!target) return;

      if (target.hasAttribute("data-select")) {
        this.answer(target.dataset.qid, JSON.parse(target.dataset.value));
      } else if (target.hasAttribute("data-toggle")) {
        const value = JSON.parse(target.dataset.value);
        if (this.pendingMulti.has(value)) this.pendingMulti.delete(value);
        else this.pendingMulti.add(value);
        target.classList.toggle("luggage-finder__option--selected");
        target.setAttribute("aria-pressed", String(this.pendingMulti.has(value)));
      } else if (target.hasAttribute("data-toggle-liked") || target.hasAttribute("data-toggle-avoided")) {
        this.toggleBrand(target);
      } else if (target.hasAttribute("data-continue")) {
        this.answer(target.dataset.qid, Array.from(this.pendingMulti));
      } else if (target.hasAttribute("data-continue-brands")) {
        this.answerBrands();
      } else if (target.hasAttribute("data-continue-budget")) {
        this.answerBudget();
      } else if (target.hasAttribute("data-back")) {
        this.back();
      } else if (target.hasAttribute("data-skip")) {
        this.skip();
      } else if (target.hasAttribute("data-see-matches")) {
        this.showResults(true);
      } else if (target.hasAttribute("data-restart")) {
        this.restart();
      } else if (target.hasAttribute("data-results-back")) {
        this.backFromResults();
      } else if (target.hasAttribute("data-retry")) {
        this.retryLoad();
      } else if (target.hasAttribute("data-result-click")) {
        track("luggage_finder_result_click", {
          title: target.dataset.title,
          badges: JSON.parse(target.dataset.badges || "[]"),
        });
      }
    }

    // A brand button in either the "love" or "avoid" group. A brand can
    // never be marked both at once (spec:257), so selecting it in one group
    // clears it from the other -- including the other group's own button,
    // found by its shared data-value, so both halves of the screen stay in
    // sync without a full re-render (which would steal focus off the button
    // the customer just pressed).
    toggleBrand(target) {
      const isLiked = target.hasAttribute("data-toggle-liked");
      const value = JSON.parse(target.dataset.value);
      const mySet = isLiked ? this.pendingBrandsLiked : this.pendingBrandsAvoided;
      const otherSet = isLiked ? this.pendingBrandsAvoided : this.pendingBrandsLiked;
      const otherAttr = isLiked ? "data-toggle-avoided" : "data-toggle-liked";

      if (mySet.has(value)) {
        mySet.delete(value);
      } else {
        mySet.add(value);
        if (otherSet.has(value)) {
          otherSet.delete(value);
          const otherBtn = Array.from(this.querySelectorAll(`[${otherAttr}]`))
            .find((el) => JSON.parse(el.dataset.value) === value);
          if (otherBtn) {
            otherBtn.classList.remove("luggage-finder__option--selected");
            otherBtn.setAttribute("aria-pressed", "false");
          }
        }
      }
      target.classList.toggle("luggage-finder__option--selected", mySet.has(value));
      target.setAttribute("aria-pressed", String(mySet.has(value)));
    }

    isSelectedSingle(question, option) {
      return JSON.stringify(this.answers[question.id]) === JSON.stringify(option.value);
    }

    // Plain buttons throughout -- no role="radio"/"radiogroup" or
    // role="checkbox". A radio/checkbox role promises arrow-key and Space
    // handling that this component doesn't implement; a fake role is worse
    // for screen-reader users than an honest one. aria-pressed communicates
    // the selected state on every option button (single and multi alike),
    // which is the correct ARIA pattern for a toggle button.
    renderOptions(question) {
      if (question.type === "brands") return this.renderBrandsOptions(question);
      if (question.type === "range") return this.renderBudgetOptions(question);

      if (question.type === "single") {
        return `
          <div class="luggage-finder__options" role="group" aria-label="${escapeAttr(this.questionTitle(question))}">
            ${question.options.map((opt) => {
              const selected = this.isSelectedSingle(question, opt);
              return `
                <button type="button"
                  class="luggage-finder__option${selected ? " luggage-finder__option--selected" : ""}"
                  data-select data-qid="${escapeAttr(question.id)}"
                  data-value='${escapeAttr(JSON.stringify(opt.value))}'
                  aria-pressed="${selected}">
                  ${escapeHtml(opt.label)}
                </button>`;
            }).join("")}
          </div>`;
      }

      return `
        <div class="luggage-finder__options luggage-finder__options--multi" role="group" aria-label="${escapeAttr(this.questionTitle(question))}">
          ${question.options.map((opt) => {
            const toggled = this.pendingMulti.has(opt.value);
            return `
              <button type="button"
                class="luggage-finder__option luggage-finder__option--toggle${toggled ? " luggage-finder__option--selected" : ""}"
                data-toggle data-qid="${escapeAttr(question.id)}"
                data-value='${escapeAttr(JSON.stringify(opt.value))}'
                aria-pressed="${toggled}">
                ${escapeHtml(opt.label)}
              </button>`;
          }).join("")}
        </div>
        ${question.notSure ? `
          <button type="button" class="luggage-finder__option luggage-finder__no-preference"
            data-select data-qid="${escapeAttr(question.id)}"
            data-value='${escapeAttr(JSON.stringify(question.notSure.value))}'
            aria-pressed="${this.isSelectedSingle(question, question.notSure)}">
            ${escapeHtml(question.notSure.label)}
          </button>` : ""}
        <button type="button" class="btn btn--primary luggage-finder__continue"
          data-continue data-qid="${escapeAttr(question.id)}">
          Continue
        </button>`;
    }

    // Two independent multi-select groups sharing one brand list -- brands
    // you love (soft boost) and brands to avoid (hard exclusion), per
    // spec:257. A single flat button list can't represent "in neither group"
    // vs "in one specific group", which is why this isn't just renderOptions()
    // with a wider option set. Kept compact (two stacked groups, no grid) so
    // the eighth and last screen still fits comfortably on a phone.
    renderBrandsGroup(question, label, hint, pendingSet, toggleAttr) {
      return `
        <div class="luggage-finder__brand-group">
          <h3 class="luggage-finder__brand-group-title">${escapeHtml(label)}</h3>
          ${hint ? `<p class="luggage-finder__brand-group-hint">${escapeHtml(hint)}</p>` : ""}
          <div class="luggage-finder__options luggage-finder__options--multi" role="group" aria-label="${escapeAttr(label)}">
            ${question.options.map((opt) => {
              const toggled = pendingSet.has(opt.value);
              return `
                <button type="button"
                  class="luggage-finder__option luggage-finder__option--toggle${toggled ? " luggage-finder__option--selected" : ""}"
                  ${toggleAttr} data-qid="${escapeAttr(question.id)}"
                  data-value='${escapeAttr(JSON.stringify(opt.value))}'
                  aria-pressed="${toggled}">
                  ${escapeHtml(opt.label)}
                </button>`;
            }).join("")}
          </div>
        </div>`;
    }

    renderBrandsOptions(question) {
      return `
        ${this.renderBrandsGroup(question, "Brands you love", "Or have been eyeing -- gives them a boost, doesn't rule anything else out.", this.pendingBrandsLiked, "data-toggle-liked")}
        ${this.renderBrandsGroup(question, "Brands to avoid", "We won't show these at all.", this.pendingBrandsAvoided, "data-toggle-avoided")}
        <button type="button" class="btn btn--primary luggage-finder__continue"
          data-continue-brands>
          Continue
        </button>`;
    }

    // Two independent dropdowns (floor + ceiling) sharing one screen, same
    // "two related controls, one screen" pattern the brands screen
    // established above -- native <select> rather than button grids, since
    // there's no natural upper bound on how many round-dollar figures a
    // catalog this wide (see QUESTIONS' comment) could need, and a `<select>`
    // degrades to a plain, always-usable control at any viewport width
    // (including the 375px floor this screen has to work at) with no
    // flex-wrap math required. "Not sure" is rendered as an ordinary
    // data-select option (value: null) below the two dropdowns, so it reuses
    // answer()'s existing handling rather than a bespoke skip control.
    //
    // Order matters (owner, 2026-08-26): the two dropdowns and "Not sure" are
    // all ways of answering the question, so they group together, and
    // Continue sits last and alone -- it is the way OUT of the screen, not a
    // fourth answer. Same shape the multi-select and brands screens use.
    renderBudgetOptions(question) {
      const minId = `${question.id}-min`;
      const maxId = `${question.id}-max`;
      const renderSelect = (id, dataAttr, spec, pendingValue) => `
        <div class="luggage-finder__budget-field">
          <label class="luggage-finder__budget-label" for="${id}">${escapeHtml(spec.label)}</label>
          <select id="${id}" class="luggage-finder__budget-select" ${dataAttr}>
            ${spec.options.map((opt) => `
              <option value="${opt.value}"${opt.value === pendingValue ? " selected" : ""}>
                ${escapeHtml(opt.label)}
              </option>`).join("")}
          </select>
        </div>`;

      return `
        <div class="luggage-finder__budget-range">
          ${renderSelect(minId, "data-budget-min", question.min, this.pendingBudgetMin)}
          ${renderSelect(maxId, "data-budget-max", question.max, this.pendingBudgetMax)}
        </div>
        <button type="button" class="luggage-finder__option luggage-finder__no-preference"
          data-select data-qid="${escapeAttr(question.id)}"
          data-value='${escapeAttr(JSON.stringify(question.notSure.value))}'
          aria-pressed="${this.isSelectedSingle(question, question.notSure)}">
          ${escapeHtml(question.notSure.label)}
        </button>
        <button type="button" class="btn btn--primary luggage-finder__continue"
          data-continue-budget data-qid="${escapeAttr(question.id)}">
          Continue
        </button>`;
    }

    // Hidden below 1024px in CSS rather than skipped here: the markup is a
    // few hundred bytes and keeping one render path avoids the component
    // needing to know the viewport width.
    //
    // No aria-live. The contents change on every single answer, and
    // announcing three product names each time would bury the question
    // itself; screen-reader users get the same products, in the same order,
    // on the results screen.
    renderPanel() {
      const { count, total, narrowed, previews } = matchPanel(this.products || [], this.answers);
      if (!previews.length) return "";
      const headline = !narrowed
        ? `<strong>${total}</strong> bags to choose from`
        : count === 0
          ? `<strong>No</strong> exact match yet`
          : `<strong>${count}</strong> of ${total} still match`;
      return `
        <aside class="luggage-finder__panel" aria-hidden="true">
          <p class="luggage-finder__panel-count">${headline}</p>
          <p class="luggage-finder__panel-title">${count === 0 && narrowed ? "Closest so far" : "Leading picks so far"}</p>
          <div class="luggage-finder__panel-grid">
            ${previews.map((p) => `
              <figure class="luggage-finder__panel-item">
                <img src="${escapeAttr(p.image)}" alt="" loading="lazy">
                <figcaption>${escapeHtml(p.title)}</figcaption>
              </figure>`).join("")}
          </div>
          <p class="luggage-finder__panel-note">${count === 0 && narrowed
            ? "Nothing matches every answer, so these are the nearest. You will still get recommendations."
            : "Reshuffles as you answer. Nothing is settled until the end."}</p>
        </aside>`;
    }

    render() {
      const questions = this.questions;
      const question = questions[this.step];
      const total = questions.length;
      const pct = Math.round(((this.step + 1) / total) * 100);
      const panelHtml = this.renderPanel();
      const budgetIndex = questions.findIndex((q) => q.id === "budget");
      const showSeeMatches = budgetIndex !== -1 && this.step >= budgetIndex;

      this.pendingMulti = new Set(
        question.type === "multi" ? (this.answers[question.id] || []) : [],
      );
      // Re-seeded from the confirmed answer on every render (not just once)
      // so returning to this screen via Back after a Continue shows the
      // brands exactly as last confirmed, same as pendingMulti above.
      this.pendingBrandsLiked = new Set(question.type === "brands" ? (this.answers.brandsLiked || []) : []);
      this.pendingBrandsAvoided = new Set(question.type === "brands" ? (this.answers.brandsAvoided || []) : []);
      // Re-seeded from the confirmed [lo, hi] on every render, same reasoning
      // as brands above. Defaults to the widest possible range (No minimum /
      // No maximum -- each dropdown's own first/last option) rather than an
      // arbitrary band, so a customer who never touches either <select> and
      // hits Continue gets exactly "no budget preference" for whichever side
      // they left alone.
      if (question.type === "range") {
        const confirmed = Array.isArray(this.answers[question.id]) ? this.answers[question.id] : null;
        this.pendingBudgetMin = confirmed ? confirmed[0] : question.min.options[0].value;
        this.pendingBudgetMax = confirmed ? confirmed[1] : question.max.options[question.max.options.length - 1].value;
      } else {
        this.pendingBudgetMin = null;
        this.pendingBudgetMax = null;
      }

      // Top restart link (2026-08-20, owner request: reachable during the
      // quiz, not just from results). Hidden on question 1 -- with no
      // answers recorded yet, restarting from there would do nothing, and
      // omitting it keeps the very first screen (already carrying the intro
      // copy above) from being cluttered by a control with nothing to undo.
      // Reuses .luggage-finder__restart's existing quiet-text-link look
      // unchanged (per the brief); the --top modifier only places it, same
      // pattern as .luggage-finder-drawer's descendant overrides elsewhere
      // in this file. handleClick()'s existing data-restart branch already
      // covers this button with no changes needed there, and restart()
      // already fires the luggage_finder_restart dataLayer event, so both
      // this control and the results-screen ones stay in sync for free.
      this.setScreen(`
        <div class="luggage-finder color-scheme color-scheme--1">
          ${this.introMarkup()}
          <div class="luggage-finder__header">
            ${this.step > 0
              ? '<button type="button" class="luggage-finder__restart luggage-finder__restart--top" '
                + 'data-restart>Start over</button>'
              : ""}
            <div class="luggage-finder__progress" role="progressbar"
              aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100"
              aria-label="Quiz progress" aria-live="polite">
              <span class="luggage-finder__progress-bar" style="width:${pct}%"></span>
            </div>
            <p class="luggage-finder__step-count">Question ${this.step + 1} of ${total}</p>
          </div>

          <div class="luggage-finder__screen${panelHtml ? " luggage-finder__screen--with-panel" : ""}">
            <div class="luggage-finder__question" data-question="${escapeAttr(question.id)}">
              <h2 class="luggage-finder__title" tabindex="-1" data-focus-heading>${escapeHtml(this.questionTitle(question))}</h2>
              ${hintFor(question)
                ? `<p class="luggage-finder__hint">${escapeHtml(hintFor(question))}</p>`
                : ""}
              ${this.renderOptions(question)}
              ${question.id === "side" ? hardsideExplainer(this.getAttribute("data-explainer-image"), this.getAttribute("data-explainer-image-alt")) : ""}
            </div>
            ${panelHtml}
          </div>

          <div class="luggage-finder__actions">
            <div class="luggage-finder__actions-left">
              ${this.step > 0
                ? '<button type="button" class="btn btn--secondary luggage-finder__back" data-back>Back</button>'
                : ""}
            </div>
            <div class="luggage-finder__actions-right">
              <button type="button" class="luggage-finder__skip" data-skip>Skip this question</button>
              ${showSeeMatches
                ? '<button type="button" class="btn btn--secondary luggage-finder__see-matches" '
                  + 'data-see-matches>See my matches</button>'
                : ""}
            </div>
          </div>
        </div>
      `);
    }

    // Every screen replaces innerHTML wholesale (simplest correct approach
    // for a handful of small screens with no build step), which destroys
    // focus. Moving focus to the new screen's heading -- tabindex="-1" so it
    // is programmatically focusable without joining the tab order -- is what
    // keeps a keyboard/screen-reader user inside the flow instead of being
    // dropped back at the top of the document on every answer.
    //
    // When mounted inside the site-wide drawer (assets/luggage-finder-tab.js),
    // this same wholesale replacement also orphans the theme's shared focus
    // trap: main.js's trapFocus() snapshots the drawer's first/last
    // focusable elements once, at open, and this method just replaced all of
    // them, so the trap's `lastEl` is a detached node and Tab escapes into
    // the page behind the drawer from the second screen onward. Re-running
    // the theme's own trapFocus() against the drawer (never reimplemented
    // here) against the fresh DOM fixes that, and folds the heading focus
    // into the same call since trapFocus() already focuses its
    // elementToFocus argument. Only done when actually inside an *open*
    // drawer -- the dedicated page mount (sections/luggage-finder.liquid)
    // has no modal to trap focus within, and running the trap there would
    // wrongly cage Tab inside the component on an ordinary page.
    setScreen(html) {
      this.innerHTML = html;
      const heading = this.querySelector("[data-focus-heading]");
      const drawer = this.closest("luggage-finder-drawer");
      const inOpenDrawer = drawer && drawer.getAttribute("open") !== null;
      if (inOpenDrawer && typeof window.trapFocus === "function") {
        window.trapFocus(drawer, heading || drawer);
      } else if (heading) {
        heading.focus();
      }
    }

    productMedia(product) {
      if (!product.image) return '<div class="luggage-finder__media luggage-finder__media--empty"></div>';
      return `
        <div class="luggage-finder__media">
          <img src="${escapeAttr(product.image)}" alt="${escapeAttr(product.title)}"
            loading="lazy" width="480" height="480">
        </div>`;
    }

    // Change 1 + change 3, composed: reserve one alt slot for the best
    // genuinely in-budget product when Best itself misses the stated budget
    // (pickInBudgetAlternative(), searched against the whole raw pool so it
    // can cross size/side lines Best's own eligibility filter wouldn't),
    // then fill whatever slots remain by price spread across the ordinarily-
    // eligible candidates (pickPriceSpreadAlternatives()) -- never simply
    // "the next N by score", which tends to cluster around Best's own price.
    // The reserved pick always takes precedence and is never displaced by
    // the spread step; both steps exclude Best and each other so no product
    // ever appears twice. Returns the reserved product's id (or null) so the
    // caller can label that one card distinctly from an ordinary spread pick.
    selectAlternatives(list, count = 3) {
      const best = list[0];
      const pool = (this.products || []).filter((p) => p.id !== best.id);
      let reserved = null;
      let noInBudgetMatch = false;
      if (this.answers.budget && isOverBudget(best, this.answers)) {
        reserved = pickInBudgetAlternative(pool, this.answers, [best.id]);
        noInBudgetMatch = !reserved;
      }
      const spreadSlots = count - (reserved ? 1 : 0);
      const spreadCandidates = list.slice(1).filter((p) => !reserved || p.id !== reserved.id);
      const spread = spreadSlots > 0
        ? pickPriceSpreadAlternatives(spreadCandidates, best.price, spreadSlots)
        : [];
      return {
        alts: reserved ? [reserved, ...spread] : spread,
        reservedId: reserved ? reserved.id : null,
        noInBudgetMatch,
      };
    }

    resultsMarkup(list, isClosest) {
      const best = list[0];
      const bestBadges = badgesFor(best, list, this.answers);
      const bestWhy = explain(best, this.answers);
      const outOfBudget = isOverBudget(best, this.answers);
      const { alts, reservedId, noInBudgetMatch } = this.selectAlternatives(list);

      return `
        <div class="luggage-finder color-scheme color-scheme--1">
        <div class="luggage-finder__results">
          <div class="luggage-finder__results-header">
            <button type="button" class="luggage-finder__results-back" data-results-back>
              &larr; Back to questions
            </button>
            <button type="button" class="luggage-finder__restart luggage-finder__restart--top" data-restart>
              Start over
            </button>
            <h2 class="luggage-finder__results-title" tabindex="-1" data-focus-heading>
              ${isClosest ? "Closest matches" : "Your matches"}
            </h2>
            ${isClosest
              ? '<p class="luggage-finder__results-note">Nothing matched every answer '
                + "exactly, but these come closest based on what you told us.</p>"
              : ""}
            ${outOfBudget && noInBudgetMatch
              ? `<p class="luggage-finder__results-note luggage-finder__results-note--budget">${escapeHtml(noInBudgetMessage(this.answers))}</p>`
              : outOfBudget ? OUT_OF_BUDGET_NOTE : ""}
          </div>

          <article class="luggage-finder__best-card" data-result-click
            data-title="${escapeAttr(best.title)}"
            data-badges='${escapeAttr(JSON.stringify(bestBadges))}'>
            ${bestBadges.length
              ? `<div class="luggage-finder__badges">${bestBadges.map((b) =>
                  `<span class="badge luggage-finder__badge">${escapeHtml(b)}</span>`).join("")}</div>`
              : ""}
            ${this.productMedia(best)}
            <div class="luggage-finder__best-body">
              <h3 class="luggage-finder__best-title">
                <a href="${escapeAttr(best.url)}">${escapeHtml(best.title)}</a>
              </h3>
              ${typeof best.price === "number"
                ? `<p class="luggage-finder__best-price">${formatPrice(best.price)}</p>` : ""}
              ${bestWhy.length
                ? `<ul class="luggage-finder__reasons">${bestWhy.map((w) =>
                    `<li>${escapeHtml(w)}</li>`).join("")}</ul>`
                : ""}
              <a class="btn btn--primary luggage-finder__best-cta" href="${escapeAttr(best.url)}">
                See this bag
              </a>
            </div>
          </article>

          ${alts.length ? `
          <div class="luggage-finder__alt-grid">
            ${alts.map((product) => {
              // The reserved in-budget card gets its own honest reason text
              // (naming whichever of size/side had to give) and its own
              // badge, in front of anything badgesFor() would otherwise say
              // -- the trade-off is the whole point of this slot, not a
              // footnote. Every other alt keeps the existing explain()/
              // badgesFor() behavior unchanged.
              const isReserved = product.id === reservedId;
              const why = isReserved
                ? [budgetPickNote(product, this.answers)]
                : explain(product, this.answers);
              const badges = isReserved
                ? ["Fits Your Budget", ...badgesFor(product, list, this.answers)]
                : badgesFor(product, list, this.answers);
              return `
                <article class="luggage-finder__alt-card${isReserved ? " luggage-finder__alt-card--budget" : ""}" data-result-click
                  data-title="${escapeAttr(product.title)}"
                  data-badges='${escapeAttr(JSON.stringify(badges))}'>
                  ${badges.length
                    ? `<span class="badge luggage-finder__badge">${escapeHtml(badges[0])}</span>` : ""}
                  ${this.productMedia(product)}
                  <h4 class="luggage-finder__alt-title">
                    <a href="${escapeAttr(product.url)}">${escapeHtml(product.title)}</a>
                  </h4>
                  ${typeof product.price === "number"
                    ? `<p class="luggage-finder__alt-price">${formatPrice(product.price)}</p>` : ""}
                  <p class="luggage-finder__alt-reason">
                    ${escapeHtml(why[0] || "A solid alternative based on your answers.")}
                  </p>
                  <a class="btn btn--secondary luggage-finder__alt-cta" href="${escapeAttr(product.url)}">
                    View bag
                  </a>
                </article>`;
            }).join("")}
          </div>` : ""}

          <button type="button" class="luggage-finder__restart" data-restart>Start over</button>
        </div>
        </div>
      `;
    }

    renderResults(ranked) {
      this.resultsShown = true;
      if (!ranked.length) {
        const closest = closestMatches(this.products || [], this.answers);
        if (!closest.length) {
          this.setScreen(`
            <div class="luggage-finder color-scheme color-scheme--1">
              <div class="luggage-finder__results luggage-finder__results--empty">
                <h2 class="luggage-finder__results-title" tabindex="-1" data-focus-heading>
                  No matches right now
                </h2>
                <p>We could not find a bag in stock right now that fits what you told
                us. Give us a call or stop by the shop -- we carry more than what's
                listed online, and we're happy to help in person.</p>
                <button type="button" class="btn btn--secondary luggage-finder__restart" data-restart>
                  Start over
                </button>
              </div>
            </div>`);
          return;
        }
        this.setScreen(this.resultsMarkup(closest, true));
        return;
      }
      this.setScreen(this.resultsMarkup(ranked, false));
    }

    // Failure path for showResults(): a fetch that never resolved (rejected
    // promise) or resolved but failed loadProducts()'s validation. Same
    // honest-framing voice as the no-matches empty state above, plus a
    // control that actually clears the cache and retries rather than
    // leaving the customer stuck on a dead screen.
    renderLoadError() {
      this.resultsShown = true;
      this.setScreen(`
        <div class="luggage-finder color-scheme color-scheme--1">
          <div class="luggage-finder__results luggage-finder__results--empty">
            <h2 class="luggage-finder__results-title" tabindex="-1" data-focus-heading>
              We hit a snag
            </h2>
            <p>Something went wrong loading our current bags. Give us a call or stop
            by the shop -- we're happy to help you find the right bag in person --
            or try again below.</p>
            <div class="luggage-finder__error-actions">
              <button type="button" class="btn btn--primary luggage-finder__retry" data-retry>
                Try again
              </button>
              <button type="button" class="btn btn--secondary luggage-finder__restart" data-restart>
                Start over
              </button>
            </div>
          </div>
        </div>`);
    }
  }
  customElements.define("luggage-finder-tool", LuggageFinderTool);
}

function track(event, detail) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...detail });
}
